// Inspect window — opened with P. Two modes sharing one card:
//  • REMOTE inspect: P on a selected player → their figure + equipped gear (read-only), as before.
//  • SELF stats ("My Stats" button, or P with nothing selected) → your own gear paperdoll on the
//    LEFT and a character sheet on the RIGHT: base stats, health/armor/stamina/health-regen, then
//    skills + professions. Numbers only (no formulas).

import {
  deriveArmor,
  statXpForLevel,
  STAT_HARD_CAP,
  pickaxeXpForLevel,
  MINING_LEVEL_CAP,
  skinnerXpForLevel,
  SKINNING_LEVEL_CAP,
  cookingXpForLevel,
  COOKING_LEVEL_CAP,
  fishingXpForLevel,
  FISHING_LEVEL_CAP,
  professionXpForLevel,
  professionLevelCap,
  PROFESSION_NAMES,
  healthRegenAmount,
  healthRegenIntervalMs,
  type CharacterAppearance,
  type ItemMods,
  FISHING_HAT_STYLE,
  FISH_HAT_ZONE_BONUS,
  FISH_HAT_SPEED_MULT,
} from "@ghostland/shared";
import { Paperdoll } from "./paperdoll.js";
import { injectTheme } from "./theme.js";
import { statsStore } from "../items/statsStore.js";
import { staminaStore } from "../items/staminaStore.js";
import { appearanceStore } from "../items/appearanceStore.js";
import { cosmeticStore } from "../items/cosmeticStore.js";
import { weaponStore } from "../items/weaponStore.js";
import { neckStore } from "../items/neckStore.js";

type CosmeticData = { hat: string; top: string; bottom: string; shoes: string };
let showCosmeticsAction: ((show: boolean) => void) | null = null;
/** Wire the "show cosmetics" toggle's send (from world3d). */
export function setInspectActions(a: { setShowCosmetics: (show: boolean) => void }): void {
  showCosmeticsAction = a.setShowCosmetics;
}

const STYLE_ID = "ghostland-inspect-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-insp-overlay {
  position: fixed; inset: 0; z-index: 32; display: none;
  align-items: center; justify-content: center; background: rgba(4,0,6,.62); color: var(--gl-text);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
}
#gl-insp-overlay.open { display: flex; }
#gl-insp-card {
  padding: 20px 22px 22px; background: var(--gl-panel);
  border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 16px 50px rgba(0,0,0,.7), 0 0 0 1px rgba(124,58,237,.15);
}
#gl-insp-head { display: flex; align-items: baseline; justify-content: space-between; margin: 0 0 16px; gap: 24px; }
#gl-insp-head h1 { margin: 0; font-size: 16px; letter-spacing: .16em; font-family: 'Silkscreen', ui-monospace, monospace; }
#gl-insp-head .hint { font-size: 11px; color: var(--gl-muted); }
#gl-insp-body { display: flex; gap: 16px; align-items: stretch; }
/* the character sheet (self mode only) */
#gl-insp-stats { display: none; flex-direction: column; gap: 13px; min-width: 216px; padding: 4px 2px; }
#gl-insp-overlay.self #gl-insp-stats { display: flex; }
.gl-insp-grp { display: flex; flex-direction: column; gap: 5px; }
.gl-insp-grp h2 {
  margin: 0 0 2px; font-size: 10px; letter-spacing: .2em; text-transform: uppercase; color: var(--gl-muted);
  font-family: 'Silkscreen', ui-monospace, monospace; border-bottom: 1px solid var(--gl-border); padding-bottom: 4px;
}
.gl-insp-row { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; font-size: 12px; }
.gl-insp-row .k { color: var(--gl-muted); }
.gl-insp-row .v { color: var(--gl-text); font-weight: 700; }
.gl-insp-row .v small { color: var(--gl-muted); font-weight: 400; font-size: 10px; margin-left: 3px; }
/* a level row + its XP-to-next-level progress bar */
.gl-insp-prow { display: flex; flex-direction: column; gap: 3px; }
.gl-insp-bar { height: 5px; background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 3px; overflow: hidden; }
.gl-insp-bar > i { display: block; height: 100%; background: var(--gl-selected); transition: width .2s ease; }
.gl-insp-bar.max > i { background: var(--gl-muted); }
#gl-insp-foot { display: flex; gap: 10px; margin-top: 16px; }
#gl-insp-foot button {
  flex: 1; padding: 8px 0; background: var(--gl-panel-2);
  border: 1px solid var(--gl-border); border-radius: 8px; color: var(--gl-muted); cursor: pointer; font-size: 12px;
}
#gl-insp-foot button:hover { border-color: var(--gl-border-focus); color: var(--gl-text); }
.gl-insp-costoggle { display: none; align-items: center; gap: 6px; font-size: 11px; color: var(--gl-muted); cursor: pointer; user-select: none; margin-right: auto; }
#gl-insp-overlay.self .gl-insp-costoggle { display: inline-flex; }
.gl-insp-costoggle input { cursor: pointer; }
`;

let overlay: HTMLDivElement | null = null;
let nameEl: HTMLElement | null = null;
let hintEl: HTMLElement | null = null;
let statsEl: HTMLDivElement | null = null;
let paperdoll: Paperdoll | null = null;
let ownName = "My Character";
let selfUnsubs: (() => void)[] = [];

/** Set the local player's display name (shown as the self-sheet title). Called once at boot. */
export function setInspectOwnName(name: string): void {
  ownName = name || "My Character";
}

function clearSelfSubs(): void {
  for (const u of selfUnsubs) u();
  selfUnsubs = [];
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-insp-overlay";
  overlay.innerHTML = `
    <div id="gl-insp-card">
      <div id="gl-insp-head"><h1></h1><span class="hint"></span></div>
      <div id="gl-insp-body"><div id="gl-insp-stats"></div></div>
      <div id="gl-insp-foot">
        <label class="gl-insp-costoggle"><input type="checkbox" id="gl-insp-cos" /> Show cosmetics</label>
        <button id="gl-insp-mystats" type="button">My Stats</button>
        <button id="gl-insp-close" type="button">Close</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  nameEl = overlay.querySelector("h1");
  hintEl = overlay.querySelector(".hint");
  statsEl = overlay.querySelector("#gl-insp-stats");
  paperdoll = new Paperdoll(); // read-only (no onUnequip)
  // figure goes BEFORE the stats column in the body
  overlay.querySelector("#gl-insp-body")!.insertBefore(paperdoll.root, statsEl);
  overlay.querySelector("#gl-insp-mystats")!.addEventListener("click", () => openSelfStats());
  overlay.querySelector("#gl-insp-cos")!.addEventListener("change", (e) => showCosmeticsAction?.((e.target as HTMLInputElement).checked));
  overlay.querySelector("#gl-insp-close")!.addEventListener("click", closeInspect);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeInspect(); });
}

/** Open the read-only inspect window for another player: their figure + equipped gear (incl. the
 *  rolled armored bonus, hovered on the gear cells — synced from the remote player's worn mods). */
export function openInspect(name: string, a: CharacterAppearance, hand1: string, hand2: string, mods?: { top?: ItemMods; bottom?: ItemMods }, cos?: CosmeticData, showCos = true): void {
  ensure();
  clearSelfSubs();
  overlay!.classList.remove("self");
  if (nameEl) nameEl.textContent = name;
  if (hintEl) hintEl.textContent = "inspecting · P to close";
  paperdoll!.setData(a, { hand1, hand2 }, "", undefined, mods, cos, showCos);
  overlay!.classList.add("open");
  paperdoll!.start();
}

/** Open the SELF character sheet: own gear paperdoll + a live stats column. */
export function openSelfStats(): void {
  ensure();
  clearSelfSubs();
  overlay!.classList.add("self");
  if (nameEl) nameEl.textContent = ownName;
  if (hintEl) hintEl.textContent = "your character · P to close";
  // Live figure from the local appearance + cosmetics + held weapons (worn-gear rolled mods for tooltips).
  const cosBox = overlay!.querySelector<HTMLInputElement>("#gl-insp-cos");
  const syncDoll = (): void => {
    const c = cosmeticStore.get();
    if (cosBox) cosBox.checked = c.showCosmetics;
    paperdoll!.setData(appearanceStore.get(), weaponStore.get(), neckStore.get(), undefined, statsStore.get().wornMods, { hat: c.hat, top: c.top, bottom: c.bottom, shoes: c.shoes }, c.showCosmetics);
  };
  selfUnsubs.push(appearanceStore.subscribe(syncDoll));
  selfUnsubs.push(cosmeticStore.subscribe(syncDoll));
  selfUnsubs.push(weaponStore.subscribe(syncDoll));
  selfUnsubs.push(neckStore.subscribe(syncDoll)); // show the worn pendant/charm in P
  // Live numbers: stats (base/vitals/skills) re-render on any PlayerStats/health change; the doll's
  // gear tooltips also refresh since worn mods ride the same store.
  selfUnsubs.push(statsStore.subscribe(syncDoll));
  selfUnsubs.push(statsStore.subscribe(renderSelfStats));
  selfUnsubs.push(appearanceStore.subscribe(renderSelfStats)); // worn gear (fishing hat) → refresh bonuses
  overlay!.classList.add("open");
  paperdoll!.start();
}

/** Build the character sheet (right column) from the client stat stores. Numbers only. */
function renderSelfStats(): void {
  if (!statsEl) return;
  const { stats, baseStats, mining, skinning, cooking, fishing, professions, gearArmor, statBonus } = statsStore.get();
  const row = (k: string, v: string): string => `<div class="gl-insp-row"><span class="k">${k}</span><span class="v">${v}</span></div>`;
  // Worn-gear bonus per base stat (str/dex/agi from leather armor; knowledge never bonused).
  const bonus = (k: string): number => (statBonus as Record<string, number>)[k] ?? 0;
  // A level row + XP-to-next-level progress bar. `capped` ⇒ at max level (bar full, "MAX").
  const bar = (label: string, level: number, xp: number, need: number, capped: boolean, b = 0): string => {
    const pct = capped ? 100 : Math.max(0, Math.min(100, Math.round((xp / Math.max(1, need)) * 100)));
    const lvl = `Lv ${level}${b > 0 ? ` <small style="color:#7ee08a">+${b}</small>` : ""}`; // green gear bonus
    const sub = capped ? "MAX" : `${xp} / ${need}`;
    return `<div class="gl-insp-prow">` +
      `<div class="gl-insp-row"><span class="k">${label}</span><span class="v">${lvl} <small>${sub}</small></span></div>` +
      `<div class="gl-insp-bar${capped ? " max" : ""}"><i style="width:${pct}%"></i></div></div>`;
  };
  // A base-stat track bar (xp is on the BASE level; gear bonus is annotated but doesn't add xp).
  const stat = (label: string, key: string, t: { level: number; xp: number }): string =>
    bar(label, t.level, t.xp, statXpForLevel(t.level), t.level >= STAT_HARD_CAP, bonus(key));
  const armor = deriveArmor(baseStats.strength.level + (statBonus.strength ?? 0)) + (gearArmor ?? 0);
  const stamina = Math.round(staminaStore.get().max);
  const regenAmount = healthRegenAmount(baseStats.strength.level + (statBonus.strength ?? 0), baseStats.knowledge.level);
  const regenSecs = Math.round(healthRegenIntervalMs(baseStats.dex.level + (statBonus.dex ?? 0)) / 1000);
  const profRows = (professions ?? []).length
    ? (professions ?? []).map((p) =>
        bar(PROFESSION_NAMES[p.id] ?? p.id, p.level, p.xp, professionXpForLevel(p.level), p.level >= professionLevelCap(p.id))).join("")
    : `<div class="gl-insp-row"><span class="v">None</span></div>`;
  statsEl.innerHTML =
    `<div class="gl-insp-grp"><h2>Base Stats</h2>` +
      stat("Strength", "strength", baseStats.strength) + stat("Dexterity", "dex", baseStats.dex) +
      stat("Knowledge", "knowledge", baseStats.knowledge) + stat("Agility", "agility", baseStats.agility) +
    `</div>` +
    `<div class="gl-insp-grp"><h2>Attributes</h2>` +
      row("Health", `${Math.round(stats.health)} / ${Math.round(stats.maxHealth)}`) +
      row("Armor", `${armor}`) +
      row("Stamina", `${stamina}`) +
      row("Health Regen", `+${regenAmount} / ${regenSecs}s`) +
    `</div>` +
    `<div class="gl-insp-grp"><h2>Skills</h2>` +
      bar("Skinning", skinning.level, skinning.xp, skinnerXpForLevel(skinning.level), skinning.level >= SKINNING_LEVEL_CAP) +
      bar("Mining", mining.level, mining.xp, pickaxeXpForLevel(mining.level), mining.level >= MINING_LEVEL_CAP) +
      bar("Cooking", cooking.level, cooking.xp, cookingXpForLevel(cooking.level), cooking.level >= COOKING_LEVEL_CAP) +
      bar("Fishing", fishing.level, fishing.xp, fishingXpForLevel(fishing.level), fishing.level >= FISHING_LEVEL_CAP) +
    `</div>` +
    (appearanceStore.get().hat === FISHING_HAT_STYLE
      ? `<div class="gl-insp-grp"><h2>Fishing Gear</h2>` +
          `<div class="gl-insp-row"><span class="k">Fishing Hat</span><span class="v" style="color:#7ee08a">+${(FISH_HAT_ZONE_BONUS * 10).toFixed(1)} power · \u2212${Math.round((1 - FISH_HAT_SPEED_MULT) * 100)}% fish speed</span></div>` +
        `</div>`
      : "") +
    `<div class="gl-insp-grp"><h2>Professions</h2>` +
      profRows +
    `</div>`;
}

export function closeInspect(): void {
  clearSelfSubs();
  overlay?.classList.remove("open");
  overlay?.classList.remove("self");
  paperdoll?.stop();
}

export function isInspectOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
